import { useEffect, useMemo, useState } from 'react'
import { BellRing } from 'lucide-react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'

// Painel de vencimentos: CNH, exames, registros/certificados e carteira do plano de saúde dos funcionários ativos, numa lista só

type Tipo = 'CNH' | 'EXAME' | 'CERTIFICADO' | 'PLANO'
interface Linha { tipo: Tipo; motcod: string; nome: string; cargo: string; empresa: number | null; descricao: string; numero: string; validade: string; dias: number }
interface Painel { hoje: string; limite: string; linhas: Linha[]; resumo: Record<Tipo, { vencidos: number; aVencer: number }> }

const TIPOS: { cod: Tipo; rot: string; fg: string; bg: string }[] = [
  { cod: 'CNH', rot: 'CNH', fg: '#1e40af', bg: '#dbeafe' },
  { cod: 'EXAME', rot: 'Exames médicos', fg: '#166534', bg: '#dcfce7' },
  { cod: 'CERTIFICADO', rot: 'Registros e certificados', fg: '#6b21a8', bg: '#f3e8ff' },
  { cod: 'PLANO', rot: 'Plano de saúde', fg: '#9a3412', bg: '#ffedd5' },
]
const dataBr = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')

function situacao(dias: number) {
  if (dias < 0) return { txt: `vencido há ${-dias} d`, fg: '#991b1b', bg: '#fee2e2' }
  if (dias === 0) return { txt: 'vence hoje', fg: '#991b1b', bg: '#fee2e2' }
  if (dias <= 15) return { txt: `vence em ${dias} d`, fg: '#92400e', bg: '#fef3c7' }
  return { txt: `vence em ${dias} d`, fg: '#1f2937', bg: '#f1f5f9' }
}

export default function PainelVencimentosFuncionarios() {
  const { empresas } = useEmpresa()
  const [empresaSel, setEmpresaSel] = useState('')
  const [filial, setFilial] = useState('')
  const [prazo, setPrazo] = useState(30)
  const [vencidos, setVencidos] = useState(true)
  const [tipoSel, setTipoSel] = useState<Tipo | ''>('')
  const [res, setRes] = useState<Painel | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState('')

  async function carregar() {
    setErro('')
    setCarregando(true)
    try {
      const r = await api.get('/funcionarios/relatorio/painel-vencimentos', { params: { empresas: empresaSel, filial, prazo, vencidos: vencidos ? 1 : 0 } })
      setRes(r.data)
    } catch (e: any) {
      setRes(null)
      setErro(e?.response?.data?.error || 'Erro ao carregar o painel.')
    } finally {
      setCarregando(false)
    }
  }

  useEffect(() => { carregar() }, [empresaSel, prazo, vencidos]) // eslint-disable-line react-hooks/exhaustive-deps

  const nomeEmpresa = useMemo(() => new Map(empresas.map(e => [e.empcod, e.empfan?.trim() || e.empnom?.trim() || String(e.empcod)])), [empresas])
  const linhas = res?.linhas.filter(l => !tipoSel || l.tipo === tipoSel) ?? []
  const funcionarios = new Set(linhas.map(l => l.motcod)).size

  const th: React.CSSProperties = { padding: '0.5rem 0.6rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', background: '#1b3f7b', textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.82rem', borderBottom: '1px solid #eef2f7', textAlign: 'left', whiteSpace: 'nowrap' }
  // mesma altura do seletor de estabelecimento, pra a linha de filtros ficar alinhada
  const campo: React.CSSProperties = { height: 34, padding: '0 0.7rem', fontSize: '0.82rem' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <BellRing size={18} color="#fff" />
            </span>
            <div>
              <h2>Painel de Vencimentos</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>CNH, exames, registros e certificados e plano de saúde dos funcionários ativos</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.8rem 1.2rem', alignItems: 'flex-end' }}>
          <div className="field" style={{ width: 280, marginBottom: 0 }}>
            <label>Estabelecimento</label>
            <select value={empresaSel} onChange={e => setEmpresaSel(e.target.value)} style={campo}>
              <option value="">Todos</option>
              {empresas.map(e => <option key={e.empcod} value={e.empcod}>{e.empcod} — {e.empfan?.trim() || e.empnom?.trim()}</option>)}
            </select>
          </div>
          <div className="field" style={{ width: 80, marginBottom: 0 }}>
            <label>Filial</label>
            <input value={filial} onChange={e => setFilial(e.target.value)} onBlur={carregar} onKeyDown={e => { if (e.key === 'Enter') carregar() }}
              maxLength={2} placeholder="todas" style={campo} />
          </div>
          <div className="field" style={{ width: 170, marginBottom: 0 }}>
            <label>Vencendo nos próximos</label>
            <select value={prazo} onChange={e => setPrazo(Number(e.target.value))} style={campo}>
              <option value={30}>30 dias</option>
              <option value={60}>60 dias</option>
              <option value={90}>90 dias</option>
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', height: 34, fontSize: '0.82rem', fontWeight: 600, color: '#1f2937', cursor: 'pointer' }}>
            <input type="checkbox" checked={vencidos} onChange={e => setVencidos(e.target.checked)} style={{ width: 16, height: 16, margin: 0 }} /> Mostrar já vencidos
          </label>
          <button className="btn btn-primary" onClick={carregar} disabled={carregando} style={{ height: 34 }}>{carregando ? 'Atualizando...' : 'Atualizar'}</button>
        </div>

        {erro && (
          <div style={{ margin: '0 1.2rem 1rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
            {erro}
          </div>
        )}
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo" style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '0.7rem' }}>
              {TIPOS.map(t => {
                const r = res.resumo[t.cod]
                const ativo = tipoSel === t.cod
                return (
                  <button key={t.cod} type="button" onClick={() => setTipoSel(ativo ? '' : t.cod)}
                    style={{ textAlign: 'left', cursor: 'pointer', padding: '0.7rem 0.9rem', borderRadius: 10, background: '#fff', border: `2px solid ${ativo ? t.fg : '#e2e8f0'}`, borderLeft: `6px solid ${t.fg}` }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 800, color: t.fg, textTransform: 'uppercase' }}>{t.rot}</div>
                    <div style={{ fontSize: '1.7rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>{r.vencidos + r.aVencer}</div>
                    <div style={{ fontSize: '0.74rem', fontWeight: 700 }}>
                      <span style={{ color: '#991b1b' }}>{r.vencidos} vencido(s)</span>
                      <span style={{ color: '#94a3b8' }}> · </span>
                      <span style={{ color: '#92400e' }}>{r.aVencer} a vencer</span>
                    </div>
                  </button>
                )
              })}
            </div>

            {linhas.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nada vencendo no período.</div>}

            {linhas.length > 0 && (
              <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>Tipo</th><th style={th}>Código</th><th style={th}>Funcionário</th><th style={th}>Cargo/Função</th>
                      <th style={th}>Estab.</th><th style={th}>Documento</th><th style={th}>Número</th><th style={th}>Validade</th><th style={th}>Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((l, i) => {
                      const t = TIPOS.find(x => x.cod === l.tipo)!
                      const s = situacao(l.dias)
                      return (
                        <tr key={`${l.tipo}-${l.motcod}-${l.descricao}-${l.validade}-${l.numero}`} style={{ background: i % 2 ? '#f5f9fd' : '#fff' }}>
                          <td style={td}><span style={{ padding: '0.12rem 0.55rem', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, color: t.fg, background: t.bg }}>{t.rot}</span></td>
                          <td style={{ ...td, fontWeight: 600 }}>{l.motcod}</td>
                          <td style={{ ...td, fontWeight: 600 }}>{l.nome}</td>
                          <td style={td}>{l.cargo}</td>
                          <td style={td}>{l.empresa != null ? nomeEmpresa.get(l.empresa) ?? l.empresa : ''}</td>
                          <td style={td}>{l.descricao}</td>
                          <td style={td}>{l.numero}</td>
                          <td style={{ ...td, fontWeight: 700, color: l.dias < 0 ? '#b91c1c' : '#1b3f7b' }}>{dataBr(l.validade)}</td>
                          <td style={td}><span style={{ padding: '0.12rem 0.55rem', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, color: s.fg, background: s.bg }}>{s.txt}</span></td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ borderRadius: 12, padding: '0.8rem 1.1rem', background: 'linear-gradient(135deg, #0f2a52, #1b3f7b)', color: '#fff', fontWeight: 800, letterSpacing: '0.02em' }}>
              TOTAL: {linhas.length} VENCIMENTO(S) · {funcionarios} FUNCIONÁRIO(S)
            </div>
            <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
              Só funcionários ativos. Exame sem meses de validade cadastrados não entra. Plano de saúde: considera a última carteira de cada funcionário, e carteira sem data final
              é vigente; as já encerradas só aparecem se terminaram nos últimos 30 dias. Clique num cartão para filtrar por categoria.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
